import React, { useContext, useMemo, useState } from 'react';

import { Box, Fade, Paper, Typography } from '@material-ui/core';
import { OnboardingStepTitle } from '@vyce/core/src/components';
import { AppCheckbox } from '@vyce/core/src/components/inputs';
import { DeviceContext } from '@vyce/core/src/contexts';
import { TimeAndAttendanceLocation } from '@vyce/core/src/types';

interface Props {
  locations: TimeAndAttendanceLocation[];
  selectedLocations?: TimeAndAttendanceLocation[];
  setSelectedLocations: (locations: TimeAndAttendanceLocation[]) => void;
  /** Render only the checkbox list, without the Paper card and title. */
  embedded?: boolean;
}

const getLocationKey = (location: TimeAndAttendanceLocation) => location.uuid ?? location.name;

export const FormLocationsStep: React.FC<Props> = ({
  locations,
  selectedLocations,
  setSelectedLocations,
  embedded,
}) => {
  const { isMobile } = useContext(DeviceContext);
  const [internalSelected, setInternalSelected] = useState<TimeAndAttendanceLocation[]>([]);
  const selected = selectedLocations ?? internalSelected;

  const sortedLocations = useMemo(() => {
    return [...(locations || [])].sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? ''));
  }, [locations]);

  const selectedKeys = useMemo(() => new Set(selected.map(getLocationKey)), [selected]);

  const updateSelected = (next: TimeAndAttendanceLocation[]) => {
    if (!selectedLocations) {
      setInternalSelected(next);
    }
    setSelectedLocations(next);
  };

  const toggleLocation = (location: TimeAndAttendanceLocation) => {
    const key = getLocationKey(location);
    const next = selectedKeys.has(key)
      ? selected.filter(item => getLocationKey(item) !== key)
      : [...selected, location];
    updateSelected(next);
  };

  const list = (
    <Box display="flex" flexDirection="column" marginTop={embedded ? 0 : 2} gridGap={12}>
      {!sortedLocations.length ? (
        <Typography color="textSecondary">No Locations found.</Typography>
      ) : (
        sortedLocations.map(location => {
          const checked = selectedKeys.has(getLocationKey(location));
          return (
            <Box
              key={getLocationKey(location)}
              display="flex"
              alignItems={isMobile ? 'flex-start' : 'center'}
              gridGap={8}
            >
              <AppCheckbox
                checked={checked}
                onChange={() => toggleLocation(location)}
                variant="rectangle"
                color="primary"
              />
              <Box display="flex" flexDirection="column">
                <Typography style={{ fontSize: 15, lineHeight: '24px' }}>{location.name}</Typography>
              </Box>
            </Box>
          );
        })
      )}
    </Box>
  );

  if (embedded) {
    return list;
  }

  return (
    <Fade in={true} timeout={200}>
      <div>
        <Paper variant="outlined" style={{ padding: 24 }}>
          <OnboardingStepTitle text="Which Locations?" />
          {list}
        </Paper>
      </div>
    </Fade>
  );
};
